import { useMemo, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import type { LicenseSkuSummary, UserInsightsSummary } from '@/types'
import { useTheme } from '@/context/ThemeContext'
import { composeLicenseWasteBrief, type LicenseWasteSeverity } from '@/lib/license-waste-brief'

interface Props {
  licenses: LicenseSkuSummary[]
  insights?: UserInsightsSummary | null
  isLoading: boolean
}

type UtilTier = 'healthy' | 'moderate' | 'under' | 'idle'
type FilterMode = 'all' | 'paid' | 'free' | 'idle'

export default function LicenseUtilization({ licenses, insights, isLoading }: Props) {
  useTheme()
  const [filter, setFilter] = useState<FilterMode>('all')
  const [tableExpanded, setTableExpanded] = useState(true)

  const sorted = useMemo(
    () => [...licenses].sort((a, b) => b.utilizationPercentage - a.utilizationPercentage),
    [licenses],
  )
  const paid = useMemo(() => sorted.filter(l => !isFreeOrTrialSku(l.skuPartNumber)), [sorted])
  const free = useMemo(() => sorted.filter(l => isFreeOrTrialSku(l.skuPartNumber)), [sorted])

  // Leaderboard: paid SKUs with non-trivial idle spend, top 5 by $/mo.
  const leaders = useMemo(
    () =>
      paid
        .filter(l => (l.estimatedMonthlyWasteUsd ?? 0) > 0)
        .sort((a, b) => (b.estimatedMonthlyWasteUsd ?? 0) - (a.estimatedMonthlyWasteUsd ?? 0))
        .slice(0, 5),
    [paid],
  )

  // Tier rollup used by the mix-strip legend.
  const tierCounts = useMemo(() => countByTier(paid), [paid])

  const totalPaidSeats = useMemo(
    () => paid.reduce((sum, l) => sum + l.totalLicenses, 0),
    [paid],
  )

  const visibleRows = useMemo(() => {
    switch (filter) {
      case 'paid':
        return paid
      case 'free':
        return free
      case 'idle':
        return paid.filter(l => (l.estimatedMonthlyWasteUsd ?? 0) > 0)
      case 'all':
      default:
        return sorted
    }
  }, [filter, paid, free, sorted])

  const brief = composeLicenseWasteBrief({
    totalMonthlyWasteUsd: insights?.totalEstimatedMonthlyWasteUsd ?? null,
    totalAnnualWasteUsd: insights?.totalEstimatedAnnualWasteUsd ?? null,
    topIdleSkus: insights?.topIdleSkus,
    totalIdleSeats: insights?.totalLicensesUnassigned,
    tenantId: insights?.tenantId,
  })

  if (isLoading) return <LoadingSkeleton />
  if (licenses.length === 0) return <EmptyState />

  return (
    <div className="space-y-8">
      <VerdictCard brief={brief} />

      <SectionHead
        title={
          <>
            License <em>mix.</em>
          </>
        }
        meta={`${paid.length} paid SKUs · ${totalPaidSeats.toLocaleString()} seats`}
      />
      <MixStrip paid={paid} tierCounts={tierCounts} freeCount={free.length} />

      {leaders.length > 0 && (
        <>
          <SectionHead
            title={
              <>
                Where seats are <em>sitting idle.</em>
              </>
            }
            meta="Top 5 by monthly waste"
          />
          <Leaderboard rows={leaders} totalMonthlyWaste={insights?.totalEstimatedMonthlyWasteUsd ?? 0} />
        </>
      )}

      <SectionHead
        title={
          <>
            All <em>SKUs.</em>
          </>
        }
        meta={`${sorted.length} total · ${paid.length} paid · ${free.length} free / trial`}
      />
      <FilterChips
        active={filter}
        onChange={setFilter}
        counts={{ all: sorted.length, paid: paid.length, free: free.length, idle: leaders.length }}
      />
      <button
        onClick={() => setTableExpanded(!tableExpanded)}
        className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 hover:text-slate-400 transition-colors"
      >
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${tableExpanded ? 'rotate-180' : ''}`} />
        {tableExpanded ? 'Hide table' : 'Show table'} ({visibleRows.length})
      </button>
      {tableExpanded && <SkuTable rows={visibleRows} />}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* VerdictCard                                                                */
/* -------------------------------------------------------------------------- */

function VerdictCard({ brief }: { brief: ReturnType<typeof composeLicenseWasteBrief> }) {
  return (
    <section
      aria-labelledby="license-spend-headline"
      className="rounded-[4px] border p-9 shadow-sm"
      style={{
        background: 'var(--color-paper)',
        borderColor: 'var(--color-line)',
      }}
    >
      <span
        className="inline-flex items-center gap-2.5 rounded-sm px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.28em] text-white"
        style={{ background: 'var(--color-navy)' }}
      >
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: 'var(--tone-accent, var(--color-secondary-sand-300))', boxShadow: '0 0 6px var(--tone-accent, var(--color-secondary-sand-300))' }}
        />
        License Spend
      </span>

      <h1
        id="license-spend-headline"
        className="mt-5 font-display font-extrabold leading-[0.96] tracking-[-0.025em] [&_em]:italic [&_em]:font-semibold"
        style={{ fontSize: 'clamp(36px, 4.5vw, 52px)', color: 'var(--color-navy)' }}
      >
        {brief.headlineMonthly} <em style={{ color: 'var(--tone-accent-deep, var(--color-secondary-sand-500))' }}>is sitting idle.</em>
      </h1>

      <div className={`mt-4 text-[15px] italic leading-[1.5] ${getVerdictTextColor(brief.severity)}`}>
        &ldquo;{brief.verdict}&rdquo;
      </div>

      <p className="mt-4 max-w-[640px] text-[15px] leading-[1.55]" style={{ color: 'var(--color-navy)' }}>
        {brief.leadSentence}
      </p>

      <p
        className="mt-6 border-t pt-4 text-[11px] font-semibold uppercase tracking-[0.16em]"
        style={{ borderColor: 'var(--color-rule)', color: 'var(--color-mute)' }}
      >
        {brief.byline}
      </p>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* SectionHead                                                                */
/* -------------------------------------------------------------------------- */

function SectionHead({ title, meta }: { title: React.ReactNode; meta: string }) {
  return (
    <div className="mt-4 flex items-baseline justify-between border-b-[4px] border-double pb-3.5" style={{ borderColor: 'var(--color-navy)' }}>
      <h2
        className="font-display text-[22px] font-bold tracking-[-0.015em] [&_em]:italic [&_em]:font-medium"
        style={{ color: 'var(--color-navy)' }}
      >
        <span className="[&_em]:[color:var(--tone-accent-deep,var(--color-secondary-sand-500))]">{title}</span>
      </h2>
      <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: 'var(--color-mute)' }}>
        {meta}
      </span>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* MixStrip                                                                   */
/* -------------------------------------------------------------------------- */

function MixStrip({
  paid,
  tierCounts,
  freeCount,
}: {
  paid: LicenseSkuSummary[]
  tierCounts: Record<UtilTier, number>
  freeCount: number
}) {
  const totalSeats = paid.reduce((sum, l) => sum + l.totalLicenses, 0)

  // Order segments by tier so the bar reads healthy → idle left-to-right.
  // Within a tier, biggest seat count first so the largest blocks dominate.
  const tierOrder: Record<UtilTier, number> = { healthy: 0, moderate: 1, under: 2, idle: 3 }
  const segments = [...paid].sort((a, b) => {
    const ta = tierOf(a.utilizationPercentage)
    const tb = tierOf(b.utilizationPercentage)
    if (ta !== tb) return tierOrder[ta] - tierOrder[tb]
    return b.totalLicenses - a.totalLicenses
  })

  return (
    <div
      className="rounded-[4px] border px-7 py-6 shadow-sm"
      style={{ background: 'var(--color-paper)', borderColor: 'var(--color-line)' }}
    >
      <div className="mb-3 flex items-baseline justify-between text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: 'var(--color-mute)' }}>
        <span>
          <strong style={{ color: 'var(--color-navy)' }}>Paid seats</strong> by utilization tier
        </span>
        <span>Width = seat count · Color = tier</span>
      </div>

      <div
        className="flex h-7 overflow-hidden rounded-sm border"
        style={{ borderColor: 'var(--color-line)' }}
        role="img"
        aria-label={`License mix across ${paid.length} paid SKUs, ${totalSeats} seats`}
      >
        {segments.map(sku => {
          const widthPct = totalSeats > 0 ? (sku.totalLicenses / totalSeats) * 100 : 0
          if (widthPct < 0.5) return null
          const tier = tierOf(sku.utilizationPercentage)
          return (
            <div
              key={sku.skuId}
              className="grid place-items-center text-[10px] font-bold transition-[filter] hover:brightness-95"
              style={{
                width: `${widthPct}%`,
                background: tierBg(tier),
                color: tier === 'idle' ? '#fff' : 'var(--color-navy)',
              }}
              title={`${sku.displayName || sku.skuPartNumber}: ${sku.usedLicenses} of ${sku.totalLicenses} used (${sku.utilizationPercentage}%)`}
            >
              {widthPct >= 8 ? getShortName(sku.displayName || sku.skuPartNumber) : ''}
            </div>
          )
        })}
      </div>

      <div className="mt-3.5 flex flex-wrap gap-x-6 gap-y-1.5 text-[11px] font-semibold" style={{ color: 'var(--color-mute)' }}>
        <Legend dot="healthy" label="healthy" count={tierCounts.healthy} hint="(75%+)" />
        <Legend dot="moderate" label="moderate" count={tierCounts.moderate} hint="(50–74%)" />
        <Legend dot="under" label="under" count={tierCounts.under} hint="(25–49%)" />
        <Legend dot="idle" label="wasting" count={tierCounts.idle} hint="(<25%)" />
        {freeCount > 0 && (
          <span className="ml-auto opacity-70">Free &amp; trial SKUs ({freeCount}) excluded</span>
        )}
      </div>
    </div>
  )
}

function Legend({ dot, label, count, hint }: { dot: UtilTier; label: string; count: number; hint: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: tierBg(dot) }} />
      <strong style={{ color: 'var(--color-navy)' }}>{count}</strong> {label}{' '}
      <span className="opacity-70">{hint}</span>
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Leaderboard                                                                */
/* -------------------------------------------------------------------------- */

function Leaderboard({ rows, totalMonthlyWaste }: { rows: LicenseSkuSummary[]; totalMonthlyWaste: number }) {
  const ROMANS = ['I', 'II', 'III', 'IV', 'V']
  const leaderTotal = rows.reduce((sum, l) => sum + (l.estimatedMonthlyWasteUsd ?? 0), 0)

  return (
    <div className="rounded-[4px] border shadow-sm" style={{ background: 'var(--color-paper)', borderColor: 'var(--color-line)' }}>
      {rows.map((sku, i) => {
        const waste = sku.estimatedMonthlyWasteUsd ?? 0
        const sev = waste >= 250 ? 'crit' : waste >= 100 ? 'high' : 'med'
        const idleSeats = sku.availableLicenses
        return (
          <div
            key={sku.skuId}
            className="grid grid-cols-[64px_1fr_220px_140px] items-center gap-6 border-b px-8 py-5 transition-colors last:border-b-0 hover:bg-[color:var(--color-paper-warm)]"
            style={{ borderColor: 'var(--color-rule-soft)' }}
          >
            <div
              className="text-center font-display text-[54px] font-extrabold italic leading-[0.85] tracking-[-0.04em]"
              style={{ color: dropcapColor(sev) }}
            >
              {ROMANS[i]}
            </div>

            <div>
              <div className="text-[17px] font-bold leading-[1.25] tracking-[-0.01em]" style={{ color: 'var(--color-navy)' }}>
                {sku.displayName || sku.skuPartNumber} has {idleSeats.toLocaleString()} unused seat{idleSeats === 1 ? '' : 's'}.
              </div>
              <div className="mt-1 text-[13px] leading-[1.5]" style={{ color: 'var(--color-mute)' }}>
                <b style={{ color: 'var(--color-navy)', fontWeight: 700 }}>
                  {sku.usedLicenses.toLocaleString()} of {sku.totalLicenses.toLocaleString()} used
                </b>
                {sku.monthlyPricePerLicense != null && (
                  <>
                    {' · '}${sku.monthlyPricePerLicense.toLocaleString('en-US', { maximumFractionDigits: 2 })}/seat/mo
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: 'var(--color-mute)' }}>
                <span>Utilization</span>
                <strong style={{ color: 'var(--color-navy)' }}>{sku.utilizationPercentage}%</strong>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full" style={{ background: 'var(--color-rule-soft)' }}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${sku.utilizationPercentage}%`, background: railColor(sev) }}
                />
              </div>
            </div>

            <div className="text-right">
              <div className="font-mono text-[20px] font-bold tracking-[-0.01em] tabular-nums" style={{ color: 'var(--color-navy)' }}>
                ${Math.round(waste).toLocaleString()}
              </div>
              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: 'var(--color-mute)' }}>
                idle / mo
              </div>
            </div>
          </div>
        )
      })}

      {totalMonthlyWaste > 0 && (
        <div
          className="flex items-center justify-between border-t px-8 py-4 text-[12px]"
          style={{ borderColor: 'var(--color-rule)', color: 'var(--color-mute)' }}
        >
          <span>
            The {rows.length === 1 ? 'SKU' : `${rows.length} SKUs`} above{' '}
            {rows.length === 1 ? 'accounts' : 'account'} for{' '}
            <strong style={{ color: 'var(--color-navy)' }}>${Math.round(leaderTotal).toLocaleString()}/mo</strong> of the tenant's{' '}
            <strong style={{ color: 'var(--color-navy)' }}>${Math.round(totalMonthlyWaste).toLocaleString()}/mo</strong> idle spend.
          </span>
          <span>See full table below ↓</span>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* FilterChips                                                                */
/* -------------------------------------------------------------------------- */

function FilterChips({
  active,
  onChange,
  counts,
}: {
  active: FilterMode
  onChange: (m: FilterMode) => void
  counts: Record<FilterMode, number>
}) {
  const chips: Array<{ key: FilterMode; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'paid', label: 'Paid only' },
    { key: 'free', label: 'Free & trial' },
    { key: 'idle', label: 'Idle only' },
  ]
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map(c => {
        const isActive = active === c.key
        return (
          <button
            key={c.key}
            onClick={() => onChange(c.key)}
            className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors"
            style={{
              background: isActive ? 'var(--color-navy)' : 'var(--color-paper)',
              color: isActive ? '#fff' : 'var(--color-mute)',
              borderColor: isActive ? 'var(--color-navy)' : 'var(--color-line)',
            }}
          >
            {c.label}
            <span
              className="rounded-full px-1.5 py-px text-[10px] font-bold"
              style={{
                background: isActive ? 'rgba(255,255,255,0.15)' : 'var(--color-rule-soft)',
                color: isActive ? '#fff' : 'var(--color-mute)',
              }}
            >
              {counts[c.key]}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* SkuTable                                                                   */
/* -------------------------------------------------------------------------- */

function SkuTable({ rows }: { rows: LicenseSkuSummary[] }) {
  return (
    <div
      className="overflow-hidden rounded-[4px] border shadow-sm"
      style={{ background: 'var(--color-paper)', borderColor: 'var(--color-line)' }}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr style={{ background: 'var(--color-paper-warm)' }}>
              <Th align="left">License</Th>
              <Th align="right">Used</Th>
              <Th align="right">Total</Th>
              <Th align="right">Available</Th>
              <Th align="right">Utilization</Th>
              <Th align="right">Cost / mo</Th>
              <Th align="right">Idle / mo</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(license => {
              const isFree = isFreeOrTrialSku(license.skuPartNumber)
              const monthlyCost =
                license.monthlyPricePerLicense != null
                  ? license.totalLicenses * license.monthlyPricePerLicense
                  : null
              const idleWaste = license.estimatedMonthlyWasteUsd ?? null
              const tier = tierOf(license.utilizationPercentage)
              return (
                <tr
                  key={license.skuId}
                  className="border-b transition-colors last:border-b-0 hover:bg-[color:var(--color-paper-warm)]"
                  style={{
                    borderColor: 'var(--color-rule-soft)',
                    opacity: isFree ? 0.6 : 1,
                  }}
                >
                  <td className="px-4 py-3" style={{ color: 'var(--color-navy)' }}>
                    <span className="font-semibold">{license.displayName || license.skuPartNumber}</span>
                    {isFree && (
                      <span
                        className="ml-2 inline-block rounded-sm px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.1em]"
                        style={{ background: 'var(--color-rule-soft)', color: 'var(--color-mute)' }}
                      >
                        free
                      </span>
                    )}
                  </td>
                  <Td align="right" mono>{license.usedLicenses.toLocaleString()}</Td>
                  <Td align="right" mono>{license.totalLicenses.toLocaleString()}</Td>
                  <Td align="right" mono>{license.availableLicenses.toLocaleString()}</Td>
                  <td className="px-4 py-3 text-right">
                    <UtilPill tier={isFree ? 'muted' : tier} percent={license.utilizationPercentage} />
                  </td>
                  <Td align="right" mono>{formatUsdWhole(monthlyCost)}</Td>
                  <td
                    className="px-4 py-3 text-right font-mono tabular-nums"
                    style={{ color: idleCostColor(idleWaste, isFree) }}
                  >
                    {formatUsdMonthly(idleWaste)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div
        className="border-t px-4 py-3 text-[11px] italic leading-[1.5]"
        style={{ borderColor: 'var(--color-rule)', color: 'var(--color-mute)' }}
      >
        Cost figures use Microsoft retail list pricing. Volume, EA, or partner discounts will lower your actual spend.
      </div>
    </div>
  )
}

function Th({ children, align }: { children: React.ReactNode; align: 'left' | 'right' }) {
  return (
    <th
      className="border-b-2 px-4 py-3.5 text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap"
      style={{
        borderColor: 'var(--color-rule)',
        color: 'var(--color-mute)',
        textAlign: align,
      }}
    >
      {children}
    </th>
  )
}

function Td({ children, align, mono }: { children: React.ReactNode; align: 'left' | 'right'; mono?: boolean }) {
  return (
    <td
      className={`px-4 py-3 ${mono ? 'font-mono tabular-nums' : ''}`}
      style={{ color: 'var(--color-navy)', textAlign: align }}
    >
      {children}
    </td>
  )
}

function UtilPill({ tier, percent }: { tier: UtilTier | 'muted'; percent: number }) {
  const styles: Record<typeof tier, { bg: string; fg: string }> = {
    healthy: { bg: 'rgba(118,214,157,0.18)', fg: 'var(--color-primary-700, #4A9B6E)' },
    moderate: { bg: 'rgba(232,185,106,0.22)', fg: '#B8893F' },
    under: { bg: 'rgba(232,153,104,0.22)', fg: '#B86A35' },
    idle: { bg: 'rgba(229,115,115,0.18)', fg: '#C84B4B' },
    muted: { bg: 'var(--color-rule-soft)', fg: 'var(--color-faint)' },
  } as const
  const s = styles[tier]
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 font-mono text-[11px] font-bold tabular-nums"
      style={{ background: s.bg, color: s.fg }}
    >
      {percent}%
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* LoadingSkeleton + EmptyState                                               */
/* -------------------------------------------------------------------------- */

function LoadingSkeleton() {
  return (
    <div className="space-y-8">
      <div
        className="rounded-[4px] border p-9 shadow-sm"
        style={{ background: 'var(--color-paper)', borderColor: 'var(--color-line)' }}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-3 w-32 rounded" style={{ background: 'var(--color-rule-soft)' }} />
          <div className="h-12 w-64 rounded" style={{ background: 'var(--color-rule-soft)' }} />
          <div className="h-4 w-1/2 rounded" style={{ background: 'var(--color-rule-soft)' }} />
          <div className="h-3 w-1/3 rounded" style={{ background: 'var(--color-rule-soft)' }} />
        </div>
      </div>
      <div
        className="rounded-[4px] border p-6 shadow-sm"
        style={{ background: 'var(--color-paper)', borderColor: 'var(--color-line)' }}
      >
        <div className="animate-pulse">
          <div className="mb-4 h-7 rounded-sm" style={{ background: 'var(--color-rule-soft)' }} />
          <div className="h-3 rounded" style={{ background: 'var(--color-rule-soft)' }} />
        </div>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div
      className="rounded-[4px] border p-10 text-center shadow-sm"
      style={{ background: 'var(--color-paper)', borderColor: 'var(--color-line)' }}
    >
      <p className="text-[15px]" style={{ color: 'var(--color-mute)' }}>
        No license data available. Click "Refresh Data" to collect license information.
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function tierOf(percent: number): UtilTier {
  if (percent >= 75) return 'healthy'
  if (percent >= 50) return 'moderate'
  if (percent >= 25) return 'under'
  return 'idle'
}

function tierBg(tier: UtilTier): string {
  switch (tier) {
    case 'healthy':
      return '#76D69D'
    case 'moderate':
      return '#E8B96A'
    case 'under':
      return '#E89968'
    case 'idle':
      return '#E57373'
  }
}

function countByTier(skus: LicenseSkuSummary[]): Record<UtilTier, number> {
  const out: Record<UtilTier, number> = { healthy: 0, moderate: 0, under: 0, idle: 0 }
  for (const sku of skus) out[tierOf(sku.utilizationPercentage)] += 1
  return out
}

function dropcapColor(sev: 'crit' | 'high' | 'med'): string {
  switch (sev) {
    case 'crit':
      return '#C84B4B'
    case 'high':
      return '#B86A35'
    case 'med':
      return '#B8893F'
  }
}

function railColor(sev: 'crit' | 'high' | 'med'): string {
  switch (sev) {
    case 'crit':
      return '#E57373'
    case 'high':
      return '#E89968'
    case 'med':
      return '#E8B96A'
  }
}

function idleCostColor(amount: number | null, isFree: boolean): string {
  if (amount == null) return 'var(--color-faint)'
  if (isFree) return 'var(--color-faint)'
  if (amount <= 0) return 'var(--color-primary-700, #4A9B6E)'
  if (amount < 100) return '#B8893F'
  if (amount < 1000) return '#B86A35'
  return '#C84B4B'
}

function getVerdictTextColor(severity: LicenseWasteSeverity): string {
  switch (severity) {
    case 'clean':
      return 'text-emerald-600 dark:text-emerald-400'
    case 'low':
      return 'text-amber-600 dark:text-amber-400'
    case 'moderate':
      return 'text-orange-600 dark:text-orange-400'
    case 'large':
      return 'text-red-600 dark:text-red-400'
    case 'unknown':
    default:
      return 'text-slate-600 dark:text-slate-400'
  }
}

function getShortName(name: string): string {
  const shortcuts: Record<string, string> = {
    'Microsoft 365 E5': 'M365 E5',
    'Microsoft 365 E3': 'M365 E3',
    'Microsoft 365 Business Premium': 'M365 BP',
    'Microsoft 365 Business Basic': 'M365 BB',
    'Microsoft 365 Business Standard': 'M365 BS',
    'Office 365 E5': 'O365 E5',
    'Office 365 E3': 'O365 E3',
    'Office 365 E1': 'O365 E1',
    'Enterprise Mobility + Security E5': 'EMS E5',
    'Enterprise Mobility + Security E3': 'EMS E3',
    'Azure Active Directory Premium P2': 'AAD P2',
    'Azure Active Directory Premium P1': 'AAD P1',
    'Power BI Pro': 'PBI Pro',
    'Power BI Premium Per User': 'PBI PPU',
    'Microsoft Defender for Office 365 Plan 1': 'MDO P1',
    'Microsoft Defender for Office 365 Plan 2': 'MDO P2',
  }
  return shortcuts[name] || (name.length > 14 ? name.substring(0, 11) + '…' : name)
}

function formatUsdMonthly(value: number | undefined | null): string {
  if (value == null) return '—'
  if (value === 0) return '$0/mo'
  const rounded = value < 1 ? 1 : Math.round(value)
  return `$${rounded.toLocaleString('en-US')}/mo`
}

function formatUsdWhole(value: number | undefined | null): string {
  if (value == null) return '—'
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

const FreeLicensePatterns = ['FREE', 'TRIAL', 'VIRAL', 'EXPLORATORY', 'SEEDED', 'DEVELOPERPACK', '_DEV', 'VIRTUALUSER', '_ADHOC']
const FreeLicenseSkus = new Set(['POWER_BI_STANDARD', 'WINDOWS_STORE', 'MCOPSTNC', 'MICROSOFT_BUSINESS_CENTER', 'STREAM'])

function isFreeOrTrialSku(skuPartNumber: string): boolean {
  const upper = skuPartNumber.toUpperCase()
  if (FreeLicenseSkus.has(upper)) return true
  return FreeLicensePatterns.some(p => upper.includes(p))
}
